import { computed, ref, unref, watch } from 'vue';
import SalesTransactionPartiesAPI from 'dashboard/api/salesTransactionParties';
import { allSectionErrors, sectionErrors } from './salesPartyValidation';
import { initialProfile, sectionsForParty } from './salesPartyFormOptions';

const clone = value => JSON.parse(JSON.stringify(value || {}));

export function useSalesPartyForm({
  transactionId,
  partyId,
  party: initialParty,
  partyRole,
  partyKind,
  relationType = 'principal',
  parentPartyId = null,
  originRequired = false,
  t,
  notify,
  onSaved,
}) {
  const party = ref(initialParty || null);
  const profile = ref(initialProfile(initialParty));
  const activeSection = ref('personal');
  const errors = ref({});
  const loading = ref(false);
  const saving = ref(false);
  const error = ref('');

  const formParty = computed(() => ({
    id: party.value?.id,
    party_role: party.value?.party_role || unref(partyRole),
    party_kind: party.value?.party_kind || unref(partyKind),
    relation_type: party.value?.relation_type || unref(relationType),
    parent_party_id: party.value?.parent_party_id || unref(parentPartyId),
    position: party.value?.position,
  }));

  const requiresOrigin = computed(() => Boolean(unref(originRequired)));
  const sections = computed(() =>
    sectionsForParty(formParty.value, requiresOrigin.value)
  );
  const currentIndex = computed(() =>
    Math.max(0, sections.value.indexOf(activeSection.value))
  );
  const isEditing = computed(() => Boolean(party.value?.id || unref(partyId)));

  const load = async () => {
    const currentPartyId = party.value?.id || unref(partyId);
    if (!unref(transactionId) || !currentPartyId) return party.value;
    loading.value = true;
    error.value = '';
    try {
      const response = await SalesTransactionPartiesAPI.show(
        unref(transactionId),
        currentPartyId
      );
      party.value = response?.data?.data || null;
      profile.value = initialProfile(party.value);
      if (!sections.value.includes(activeSection.value)) {
        activeSection.value = sections.value[0];
      }
      return party.value;
    } catch (requestError) {
      error.value =
        requestError?.response?.data?.message || t('SALES_PARTIES.ERRORS.LOAD');
      notify(error.value);
      return null;
    } finally {
      loading.value = false;
    }
  };

  watch(
    sections,
    value => {
      if (!value.includes(activeSection.value)) activeSection.value = value[0];
    },
    { immediate: true }
  );

  const updateField = (section, field, value) => {
    profile.value = {
      ...profile.value,
      [section]: { ...(profile.value[section] || {}), [field]: value },
    };
    delete errors.value[section];
  };

  const updateSection = (section, value) => {
    profile.value = { ...profile.value, [section]: clone(value) };
    delete errors.value[section];
  };

  const payload = () => ({
    id: party.value?.id || unref(partyId),
    party_role: formParty.value.party_role,
    party_kind: formParty.value.party_kind,
    relation_type: formParty.value.relation_type,
    parent_party_id: formParty.value.parent_party_id,
    profile: clone(profile.value),
  });

  const validate = section => {
    const nextErrors = sectionErrors({
      profile: profile.value,
      party: formParty.value,
      section,
      t,
    });
    errors.value = { ...errors.value, [section]: nextErrors };
    return Object.keys(nextErrors).length === 0;
  };

  const save = async (section = activeSection.value) => {
    if (!validate(section)) {
      notify(t('SALES_PARTIES.ERRORS.SECTION'));
      return null;
    }

    saving.value = true;
    error.value = '';
    try {
      const response = party.value?.id
        ? await SalesTransactionPartiesAPI.saveBlock(
            unref(transactionId),
            party.value.id,
            payload()
          )
        : await SalesTransactionPartiesAPI.create(
            unref(transactionId),
            payload()
          );
      party.value = response?.data?.data || party.value;
      profile.value = initialProfile(party.value);
      onSaved?.(party.value);
      return party.value;
    } catch (requestError) {
      error.value =
        requestError?.response?.data?.message || t('SALES_PARTIES.ERRORS.SAVE');
      notify(error.value);
      return null;
    } finally {
      saving.value = false;
    }
  };

  const saveAndNext = async () => {
    const saved = await save();
    if (!saved) return false;
    const nextSection = sections.value[currentIndex.value + 1];
    if (nextSection) activeSection.value = nextSection;
    return Boolean(nextSection);
  };

  const previous = () => {
    const previousSection = sections.value[currentIndex.value - 1];
    if (previousSection) activeSection.value = previousSection;
    return Boolean(previousSection);
  };

  const sectionComplete = section =>
    Object.keys(
      sectionErrors({
        profile: profile.value,
        party: formParty.value,
        section,
        t,
      })
    ).length === 0;

  const allErrors = computed(() =>
    allSectionErrors({
      profile: profile.value,
      party: formParty.value,
      sections: sections.value,
      t,
    })
  );

  return {
    activeSection,
    allErrors,
    currentIndex,
    error,
    errors,
    formParty,
    isEditing,
    load,
    loading,
    party,
    previous,
    profile,
    save,
    saveAndNext,
    saving,
    sectionComplete,
    sections,
    updateField,
    updateSection,
  };
}
